import { useEffect, useRef } from 'react';

const isEscapeKey = event => event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27;

const getEventPath = event => {
  if (typeof event.composedPath === 'function') return event.composedPath();
  return null;
};

const useOutsideClick = (ref, callback, options = {}) => {
  const callbackRef = useRef(callback);
  const { enabled = true, detectIframeFocus = false } = options;

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return () => { };

    const handlePointerDown = event => {
      const element = ref?.current;
      if (!element) return;

      const path = getEventPath(event);
      const isInside = path ? path.includes(element) : element.contains(event.target);

      if (!isInside) callbackRef.current(event);
    };

    const handleFocusIn = event => {
      const element = ref?.current;
      if (!element) return;

      const path = getEventPath(event);
      const isInside = path ? path.includes(element) : element.contains(event.target);

      if (!isInside) callbackRef.current(event);
    };

    const handleEsc = event => {
      if (isEscapeKey(event)) callbackRef.current(event);
    };

    let iframeFocusIntervalId = null;
    let lastActiveElement = document.activeElement;
    const startIframeFocusWatcher = () => {
      if (!detectIframeFocus) return;

      iframeFocusIntervalId = window.setInterval(() => {
        const element = ref?.current;
        if (!element) return;

        const activeElement = document.activeElement;
        if (!activeElement || activeElement === lastActiveElement) return;
        lastActiveElement = activeElement;

        if (activeElement instanceof HTMLIFrameElement) {
          const isInside = element.contains(activeElement);
          if (!isInside) callbackRef.current({ type: 'iframe-focus', target: activeElement });
        }
      }, 200);
    };

    // Capture phase + pointerdown ensures we run before React's onClick handlers
    // and before DOM changes remove the original event target.
    const usePointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window;
    const downEvent = usePointerEvents ? 'pointerdown' : 'mousedown';

    document.addEventListener(downEvent, handlePointerDown, true);
    if (!usePointerEvents) document.addEventListener('touchstart', handlePointerDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('keydown', handleEsc);
    startIframeFocusWatcher();

    return () => {
      document.removeEventListener(downEvent, handlePointerDown, true);
      if (!usePointerEvents) document.removeEventListener('touchstart', handlePointerDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('keydown', handleEsc);

      if (iframeFocusIntervalId !== null) {
        window.clearInterval(iframeFocusIntervalId);
      }
    };
  }, [enabled, detectIframeFocus, ref]);
};

export default useOutsideClick;
